import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';

const TobaccoInvoiceReport = () => {
  const [formData, setFormData] = useState({
    // Monthly data for each distributor
    monthlyData: {
      january: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      february: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      march: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      april: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      may: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      june: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      july: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      august: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      september: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      october: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      november: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' },
      december: { blackCanyon: '', pbDistribution: '', stJoeDistributing: '', woodlandsHcl: '', other: '' }
    },
    // Yearly totals
    yearlyTotals: {
      '2024': { total: '' },
      '2023': { total: '' },
      '2022': { total: '' },
      '2021': { total: '' },
      '2020': { total: '' },
      '2019': { total: '' },
      '2018': { total: '' },
      '2017': { total: '' },
      '2016': { total: '' },
      '2015': { total: '' },
      '2014': { total: '' },
      '2013': { total: '' },
      '2012': { total: '' }
    }
  });

  // Retailer Information State
  const [retailerInfo, setRetailerInfo] = useState({
    retailer: 'Prairie Band Casino & Resort',
    month: 'January',
    year: '2025',
    expirationDate: ''
  });

  // Month options for dropdown
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Year options (current year and next few years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - 3 + i).toString());

  // Signature pad state and ref
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDate] = useState(new Date().toLocaleDateString());

  // Initialize canvas size after component mounts
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-container');
      if (container) {
        const width = container.clientWidth - 32;
        const height = 200;
        setCanvasSize({ width, height });

        if (sigCanvas.current) {
          const isEmpty = sigCanvas.current.isEmpty();
          if (!isEmpty) {
            const data = sigCanvas.current.toData();
            sigCanvas.current.clear();
            setTimeout(() => {
              if (sigCanvas.current) {
                sigCanvas.current.fromData(data);
              }
            }, 0);
          }
        }
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  const handleMonthlyInputChange = (month, distributor, value) => {
    const nextValue = value === '' ? '' : String(Math.max(0, Number(value)));
    setFormData(prev => ({
      ...prev,
      monthlyData: {
        ...prev.monthlyData,
        [month]: {
          ...prev.monthlyData[month],
          [distributor]: nextValue
        }
      }
    }));
  };

  const handleYearlyTotalChange = (year, value) => {
    const nextValue = value === '' ? '' : String(Math.max(0, Number(value)));
    setFormData(prev => ({
      ...prev,
      yearlyTotals: {
        ...prev.yearlyTotals,
        [year]: { total: nextValue }
      }
    }));
  };

  // Handle retailer information changes
  const handleRetailerInfoChange = (field, value) => {
    setRetailerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate monthly totals
  const calculateMonthlyTotal = (month) => {
    const data = formData.monthlyData[month];
    const blackCanyon = parseInt(data.blackCanyon) || 0;
    const pbDistribution = parseInt(data.pbDistribution) || 0;
    const stJoeDistributing = parseInt(data.stJoeDistributing) || 0;
    const woodlandsHcl = parseInt(data.woodlandsHcl) || 0;
    const other = parseInt(data.other) || 0;
    return blackCanyon + pbDistribution + stJoeDistributing + woodlandsHcl + other;
  };

  // Calculate current year total
  const calculateCurrentYearTotal = () => {
    const months = Object.keys(formData.monthlyData);
    return months.reduce((total, month) => {
      return total + calculateMonthlyTotal(month);
    }, 0);
  };

  // Calculate grand total (2012-2024)
  const calculateGrandTotal = () => {
    const years = Object.keys(formData.yearlyTotals);
    return years.reduce((total, year) => {
      const yearTotal = parseInt(formData.yearlyTotals[year].total) || 0;
      return total + yearTotal;
    }, 0);
  };

  // Format number to avoid NaN display
  const formatNumber = (value) => {
    return isNaN(value) ? 0 : value;
  };

  // Signature functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    // Signature completion logic
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the report.');
      return;
    }

    const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');

    const submissionData = {
      retailerInfo,
      formData,
      calculatedTotals: {
        totalCurrentYear: calculateCurrentYearTotal(),
        grandTotal: calculateGrandTotal()
      },
      signature: signatureData,
      signatureDate: currentDate
    };

    console.log('Form submitted:', submissionData);
    alert('Tobacco Invoice Report submitted successfully!');
  };

  const months = [
    { key: 'january', label: 'January' },
    { key: 'february', label: 'February' },
    { key: 'march', label: 'March' },
    { key: 'april', label: 'April' },
    { key: 'may', label: 'May' },
    { key: 'june', label: 'June' },
    { key: 'july', label: 'July' },
    { key: 'august', label: 'August' },
    { key: 'september', label: 'September' },
    { key: 'october', label: 'October' },
    { key: 'november', label: 'November' },
    { key: 'december', label: 'December' }
  ];

  const distributors = [
    { key: 'blackCanyon', label: 'Black Canyon' },
    { key: 'pbDistribution', label: 'PB Distribution' },
    { key: 'stJoeDistributing', label: 'St. Joe Distributing' },
    { key: 'woodlandsHcl', label: 'Woodlands/HCl' },
    { key: 'other', label: 'O' }
  ];

  const years = [
    '2024', '2023', '2022', '2021', '2020', '2019', '2018', '2017', 
    '2016', '2015', '2014', '2013', '2012'
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto bg-white shadow-md rounded-lg overflow-hidden">
        {/* Header */}
        <div className="text-center mb-8 border-b-2 border-[#840028] pb-6">
          <h1 className="text-2xl font-bold mb-4 text-[#840028]">
            Prairie Band Potawatomi Tax Commission
          </h1>
          <h2 className="text-xl font-semibold mb-4 text-[#840028]">
            Monthly Retailers Tobacco Invoice Report
          </h2>
          <div className="font-bold mb-2 text-[#840028]">
            <div>Prairie Band Casino & Resort</div>
            <div className="italic">(due on the 10th of each month)</div>
          </div>
        </div>

        {/* Form ID */}
        <div className="bg-gray-100 px-6 py-2 border-b">
          <div className="flex justify-between items-center">
            <span className="font-semibold">Retailer Report {retailerInfo.year}</span>
            <span className="text-gray-600">Tobacco Invoice Report</span>
          </div>
        </div>

        {/* Retailer Information Section */}
        <div className="border border-gray-300 p-4 m-6 mb-6">
          <div className="flex items-center mb-3">
            <label className="font-bold w-48 mr-4 text-[#840028]">Retailer:</label>
            <div className="px-3 py-2 border border-gray-300 bg-gray-50 min-w-80">
              {retailerInfo.retailer}
            </div>
          </div>

          <div className="flex items-center mb-3">
            <label className="font-bold w-24 mr-4 text-[#840028]">Month:</label>
            <select
              value={retailerInfo.month}
              onChange={(e) => handleRetailerInfoChange('month', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-32 mr-8"
            >
              {monthOptions.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>

            <label className="font-bold w-12 mr-4 text-[#840028]">Year:</label>
            <select
              value={retailerInfo.year}
              onChange={(e) => handleRetailerInfoChange('year', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-20"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center">
            <label className="font-bold w-80 mr-4 text-[#840028]">PBPN Tribal Tobacco Retailer License Expiration Date:</label>
            <input
              type="text"
              value={retailerInfo.expirationDate}
              onChange={(e) => handleRetailerInfoChange('expirationDate', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-32"
              placeholder="MM/DD/YYYY"
            />
          </div>
        </div>

        {/* Main Form Content */}
        <div className="p-6">
          {/* Monthly Purchase Table */}
          <h2 className="text-lg font-semibold mb-4 text-gray-800">
            {retailerInfo.retailer} - Cigarette Purchase Report
          </h2>

          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-300">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2 text-left font-semibold"></th>
                  {distributors.map(distributor => (
                    <th key={distributor.key} className="border border-gray-300 px-4 py-2 text-right font-semibold">
                      {distributor.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {months.map(month => (
                  <tr key={month.key}>
                    <td className="border border-gray-300 px-4 py-2 font-semibold">{month.label}</td>
                    {distributors.map(distributor => (
                      <td key={distributor.key} className="border border-gray-300 px-2 py-1">
                        <input
                          type="number"
                          value={formData.monthlyData[month.key][distributor.key] || ''}
                          onChange={(e) => handleMonthlyInputChange(month.key, distributor.key, e.target.value)}
                          min="0"
                          className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                          placeholder="0"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Current Year Total Section */}
          <div className="mt-8 p-4 border border-gray-300 bg-gray-50">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">{retailerInfo.year}</h3>
              <p className="font-semibold">{retailerInfo.retailer} Packs Purchased</p>
              <div className="mt-2 text-xl font-bold">
                Total: {formatNumber(calculateCurrentYearTotal())} packs
              </div>
            </div>
          </div>

          {/* Yearly Totals Section */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              Total number of Packs Purchased
            </h3>

            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-300">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left font-semibold">Year</th>
                    <th className="border border-gray-300 px-4 py-2 text-right font-semibold">Total Packs</th>
                  </tr>
                </thead>
                <tbody>
                  {years.map(year => (
                    <tr key={year}>
                      <td className="border border-gray-300 px-4 py-2 font-semibold">{year} Total</td>
                      <td className="border border-gray-300 px-2 py-1">
                        <input
                          type="number"
                          value={formData.yearlyTotals[year]?.total || ''}
                          onChange={(e) => handleYearlyTotalChange(year, e.target.value)}
                          min="0"
                          className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 text-right"
                          placeholder="0"
                        />
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100 font-bold">
                    <td className="border border-gray-300 px-4 py-2">Total 2012-2024</td>
                    <td className="border border-gray-300 px-4 py-2 text-right">
                      {formatNumber(calculateGrandTotal())}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Signature Section */}
          <div className="border border-gray-300 p-6 mt-8 rounded-lg">
            <h3 className="text-lg font-bold mb-4 text-center text-gray-800">Signature</h3>

            <div className="mb-4">
              <label className="font-bold block mb-2 text-gray-700">
                Sign below to certify the accuracy of this report:
              </label>
              <div
                id="signature-container"
                className="border-2 border-gray-300 rounded-lg p-4 bg-white"
              >
                {canvasSize.width > 0 && (
                  <SignatureCanvas
                    ref={sigCanvas}
                    penColor="#0000FF"
                    backgroundColor="rgb(255, 255, 255)"
                    canvasProps={{
                      width: canvasSize.width,
                      height: canvasSize.height,
                      className: 'signature-canvas w-full border border-gray-400 rounded bg-white cursor-crosshair'
                    }}
                    onBegin={handleSignatureBegin}
                    onEnd={handleSignatureEnd}
                    clearOnResize={false}
                  />
                )}
              </div>
            </div>

            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-600">
                {isSigned ? (
                  <span className="text-green-600 font-bold">✓ Signature provided</span>
                ) : (
                  <span className="text-red-600">Please sign above</span>
                )}
              </div>

              <button
                type="button"
                onClick={clearSignature}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition duration-200"
              >
                Clear Signature
              </button>
            </div>

            {/* Date Field */}
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Signed:
              </label>
              <input
                type="text"
                value={currentDate}
                readOnly
                className="w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-medium text-gray-700 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                This date is automatically set to today's date and cannot be modified
              </p>
            </div>
          </div>

          {/* Certification */}
          <div className="border-t-2 border-b-2 border-gray-500 py-6 my-8 text-center italic text-gray-700">
            <p>
              I certify that I am a duly authorized owner, partner, director, officer or representative of the licensee and that the statements made herein are true and correct to the best of my knowledge.
            </p>
          </div>

          {/* Instructions */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
            <p className="text-sm text-gray-700">
              <strong>Please print and sign report.</strong> Send Signed report and copies of invoices/IPO to:
              <br />
              Tonya Negonsott-Rodveli, Tax Director
              <br />
              Prairie Band Potawatomi Tax Commission
              <br />
              16281 Q Road
              <br />
              Mayetta, KS 66509
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex justify-center">
            <button
              onClick={handleSubmit}
              disabled={!isSigned}
              className="px-8 py-3 bg-[#840028] text-white font-medium rounded-md shadow-sm hover:bg-[#6a0020] focus:outline-none focus:ring-2 focus:ring-[#840028] focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSigned ? 'Submit Report' : 'Please Sign to Submit'}
            </button>
          </div>
        </div>
      </div>

      {/* Custom CSS for signature canvas */}
      <style jsx>{`
        .signature-canvas {
          cursor: crosshair !important;
          touch-action: none;
        }
        .signature-canvas:active {
          cursor: grabbing !important;
        }
        #signature-container {
          min-height: 200px;
        }
      `}</style>
    </div>
  );
};

export default TobaccoInvoiceReport;